import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdClose } from 'react-icons/md';
import { FaEnvelope, FaKey, FaLock } from 'react-icons/fa';
import PasswordLoginForm from './PasswordLoginForm';
import RegisterQuickForm from './RegisterQuickForm';
import OtpLoginForm from './OtpLoginForm';
import MagicLinkForm from './MagicLinkForm';
import AuthCard from './AuthCard';
import ForgotPasswordModal from './ForgotPasswordModal';
import ToastContainer, { useToast } from '../ToastContainer';
import {
  loginWithEmail,
  loginWithGoogle,
  loginWithPassword,
  registerWithPassword,
  requestPasswordReset,
  sendOTPCode,
  verifyOTPCode,
} from '../../services/api';
import logoImage from '../../assets/babysit_logo.png';

export default function AuthModal({ isOpen, onClose, initialMode = 'login', redirectTo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, removeToast, toasts } = useToast();
  const [mode, setMode] = useState(initialMode);
  const [activeTab, setActiveTab] = useState('password');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [forgotModalOpen, setForgotModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setActiveTab('password');
    setLoginError('');
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
  const redirectAfterLogin = redirectTo || location.state?.redirect || (redirectFromQuery && redirectFromQuery.startsWith('/') ? redirectFromQuery : undefined);

  const getDestination = (user) => {
    if (user?.profileComplete === false) return '/complete-profile';
    if (redirectAfterLogin && redirectAfterLogin.startsWith('/')) return redirectAfterLogin;
    return '/dashboard';
  };

  const handleLogin = async (email, password, rememberMe) => {
    setLoginError('');
    setIsSubmitting(true);
    try {
      const result = await loginWithPassword(email, password, { rememberMe });
      if (!result.success) {
        setLoginError(result.error || 'Prijava nije uspela.');
        showToast(result.error || 'Prijava nije uspela.', 'error', 5000);
        return;
      }
      showToast('Uspešno ste prijavljeni.', 'success', 2000);
      onClose?.();
      navigate(getDestination(result.user), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (payload) => {
    setIsSubmitting(true);
    try {
      const result = await registerWithPassword(payload);
      if (!result.success) {
        setLoginError(result.error || 'Registracija nije uspela.');
        showToast(result.error || 'Registracija nije uspela.', 'error', 5000);
        return;
      }
      showToast('Nalog je uspešno kreiran.', 'success', 2000);
      onClose?.();
      navigate(getDestination(result.user), { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSend = async (email) => {
    const result = await sendOTPCode(email);
    if (result.success) showToast('Kod je poslat na email.', 'success', 4000);
    return result;
  };

  const handleOtpVerify = async (email, code) => {
    const result = await verifyOTPCode(email, code);
    if (result.success) {
      showToast('Uspešno ste prijavljeni.', 'success', 2000);
      onClose?.();
      navigate(getDestination(result.user), { replace: true });
    }
    return result;
  };

  const handleMagicLink = async (email) => {
    const result = await loginWithEmail(email);
    if (result.success) showToast('Proverite email za link prijave.', 'success', 5000);
    return result;
  };

  const handleForgotPassword = async (email) => {
    return requestPasswordReset(email);
  };

  const handleGoogleSignIn = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const isPlaceholder =
      !clientId ||
      clientId === 'YOUR_GOOGLE_CLIENT_ID' ||
      clientId.toLowerCase() === 'your-google-client-id' ||
      clientId.toLowerCase().includes('your-google') ||
      !clientId.includes('.apps.googleusercontent.com');
    if (isPlaceholder) {
      showToast('Google prijava nije podešena.', 'info', 5000);
      return;
    }

    try {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Google script load timeout')), 10000);
        });
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            const result = await loginWithGoogle(response.credential, '', payload.email, payload.name, payload.picture);
            if (result.success) {
              showToast('Uspešno ste prijavljeni.', 'success', 2000);
              onClose?.();
              navigate(getDestination(result.user), { replace: true });
            } else {
              showToast(result.error || 'Google prijava nije uspela.', 'error', 5000);
            }
          } catch (error) {
            showToast('Greška pri Google prijavi.', 'error', 5000);
          }
        },
      });

      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.visibility = 'hidden';
      document.body.appendChild(buttonContainer);

      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });

      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) googleButton.click();
        else window.google.accounts.id.prompt();
      }, 250);
    } catch (error) {
      showToast('Google prijava trenutno nije dostupna.', 'error', 5000);
    }
  };

  const methodLabels = {
    password: { title: 'Prijava lozinkom', desc: 'Unesite podatke za prijavu.' },
    otp: { title: 'Prijava kodom', desc: 'Unesite email, poslaćemo vam kod.' },
    magic: { title: 'Prijava linkom', desc: 'Unesite email, poslaćemo link za prijavu.' },
  };
  const otherMethods = [
    { id: 'password', label: 'Lozinka', icon: FaLock },
    { id: 'otp', label: 'Kod na email', icon: FaKey },
    { id: 'magic', label: 'Link na email', icon: FaEnvelope },
  ].filter((m) => m.id !== activeTab);

  return (
    <div className="fixed inset-0 z-[12000] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <div
        className="w-full max-w-[min(420px,calc(100vw-1.5rem))] max-h-[min(90vh,680px)] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-white/40 dark:border-gray-700/60 animate-scale-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 relative">
          <div className="flex items-center gap-2 pr-10">
            <img src={logoImage} alt="Nannity logo" className="w-7 h-7 rounded-md" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Nannity</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-3 w-8 h-8 rounded-full border border-primary-600 text-primary-700 dark:text-primary-300 dark:border-primary-400 flex items-center justify-center hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            aria-label="Zatvori"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="px-4 py-3 sm:py-4 overflow-y-auto flex-1 min-h-0">
          <ToastContainer toasts={toasts} removeToast={removeToast} />

          {mode === 'login' ? (
            <AuthCard
              title="Prijava"
              onGoogle={handleGoogleSignIn}
              compact
              className="!shadow-none !p-0 !border-0 !bg-transparent"
            >
              <div role="tabpanel" id={`panel-${activeTab}`}>
                {activeTab !== 'password' && (
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{methodLabels[activeTab]?.title}</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{methodLabels[activeTab]?.desc}</p>
                  </div>
                )}

                {activeTab === 'password' && (
                  <PasswordLoginForm
                    onSubmit={handleLogin}
                    onForgotPassword={() => setForgotModalOpen(true)}
                    isLoading={isSubmitting}
                    error={loginError}
                    setError={setLoginError}
                    hideRegisterLink={false}
                    onRegisterClick={() => setMode('register')}
                    compact
                  />
                )}
                {activeTab === 'otp' && (
                  <OtpLoginForm
                    onSendCode={handleOtpSend}
                    onVerifyCode={handleOtpVerify}
                    onSuccess={() => {}}
                    setError={setLoginError}
                  />
                )}
                {activeTab === 'magic' && (
                  <MagicLinkForm onSendLink={handleMagicLink} setError={setLoginError} />
                )}

                {otherMethods.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-center mb-2.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Drugi načini prijave</span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {otherMethods.map((m) => {
                        const Icon = m.icon;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => { setLoginError(''); setActiveTab(m.id); }}
                            className="w-full min-w-0 px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 flex items-center justify-center gap-1.5"
                          >
                            {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </AuthCard>
          ) : (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Registracija</h2>
              <RegisterQuickForm
                onSubmit={handleRegister}
                isSubmitting={isSubmitting}
                onSwitchToLogin={() => setMode('login')}
                compact
              />
            </div>
          )}
        </div>

        <ForgotPasswordModal
          isOpen={forgotModalOpen}
          onClose={() => setForgotModalOpen(false)}
          requestPasswordReset={handleForgotPassword}
        />
      </div>
    </div>
  );
}
